import React, {useEffect, useState} from 'react';
import Modal from 'react-bootstrap/Modal';
import {Form} from 'react-bootstrap';
import {useIntl} from 'react-intl';
import ButtonCancel from '../../modules/form/ButtonCancel';
import ButtonSubmit from '../../modules/form/ButtonSubmit';
import {Customer} from '../../../Domains/Auth/Types/customer';
import {User} from '../../../Domains/Auth/Types/User';
import {useDeleteUser} from '../../../Domains/Auth/Hooks/UserManager';

type DeleteScope = 'all' | 'selected';

interface ModalDeleteUserProps {
  showModal: boolean;
  setShowModal: (show: boolean) => void;
  user: User;
  accounts: Customer[];
  customer: Customer;
}

const ModalDeleteUser = ({
  showModal,
  setShowModal,
  user,
  accounts,
  customer,
}: ModalDeleteUserProps): JSX.Element => {
  const intl = useIntl();

  const [scope, setScope] = useState<DeleteScope>('all');
  const [selectedAccountIds, setSelectedAccountIds] = useState<number[]>([customer.id]);

  const {mutate, isLoading} = useDeleteUser();

  useEffect(() => {
    if (showModal) {
      setScope('all');
      setSelectedAccountIds([customer.id]);
    }
  }, [showModal, user, customer.id]);

  const hasSeveralAccounts = accounts.length > 1;
  const userName = `${user.first_name ?? ''} ${user.name ?? ''}`.trim() || user.mail;

  const entityIds =
    scope === 'all' || !hasSeveralAccounts
      ? accounts.map((account) => account.id)
      : selectedAccountIds;

  const toggleAccount = (accountId: number, checked: boolean): void => {
    setSelectedAccountIds((oldIds) =>
      checked ? [...oldIds, accountId] : oldIds.filter((id) => id !== accountId)
    );
  };

  const handleDelete = (event: React.FormEvent): void => {
    event.preventDefault();

    mutate(
      {id: user.id, entity_ids: entityIds},
      {
        onSuccess: () => {
          setShowModal(false);
        },
      }
    );
  };

  return (
    <Modal show={showModal} onHide={() => setShowModal(false)}>
      <Modal.Header closeButton>
        <Modal.Title>{intl.formatMessage({id: 'USER_MANAGER.DELETE_MODAL.TITLE'})}</Modal.Title>
      </Modal.Header>
      <Form onSubmit={handleDelete}>
        <Modal.Body>
          {!hasSeveralAccounts ? (
            <p className="mb-0">
              {intl.formatMessage({id: 'USER_MANAGER.DELETE_MODAL.CONFIRM'}, {name: userName})}
            </p>
          ) : (
            <>
              <p>
                {intl.formatMessage({id: 'USER_MANAGER.DELETE_MODAL.QUESTION'}, {name: userName})}
              </p>
              <Form.Check
                type="radio"
                id="delete-scope-all"
                name="delete-scope"
                className="mb-3"
                checked={scope === 'all'}
                onChange={() => setScope('all')}
                label={
                  <>
                    <div className="fw-bold text-gray-800">
                      {intl.formatMessage({id: 'USER_MANAGER.DELETE_MODAL.SCOPE.ALL'})}
                    </div>
                    <div className="text-muted fs-7">
                      {intl.formatMessage({id: 'USER_MANAGER.DELETE_MODAL.SCOPE.ALL.DESCRIPTION'})}
                    </div>
                  </>
                }
              />
              <Form.Check
                type="radio"
                id="delete-scope-selected"
                name="delete-scope"
                className="mb-5"
                checked={scope === 'selected'}
                onChange={() => setScope('selected')}
                label={
                  <>
                    <div className="fw-bold text-gray-800">
                      {intl.formatMessage({id: 'USER_MANAGER.DELETE_MODAL.SCOPE.SELECTED'})}
                    </div>
                    <div className="text-muted fs-7">
                      {intl.formatMessage({
                        id: 'USER_MANAGER.DELETE_MODAL.SCOPE.SELECTED.DESCRIPTION',
                      })}
                    </div>
                  </>
                }
              />
              <Form.Label>
                {intl.formatMessage({id: 'USER_MANAGER.DELETE_MODAL.ACCOUNTS'})}
              </Form.Label>
              {accounts.map((account) => (
                <Form.Check
                  type="checkbox"
                  key={`delete-account-${account.id}`}
                  id={`delete-account-${account.id}`}
                  className="mb-2"
                  disabled={scope === 'all'}
                  checked={scope === 'all' || selectedAccountIds.includes(account.id)}
                  onChange={(event) => toggleAccount(account.id, event.target.checked)}
                  label={
                    <>
                      {account.name}{' '}
                      <small className="text-gray-400 fw-semibold">#{account.id}</small>
                    </>
                  }
                />
              ))}
              {entityIds.length === 0 && (
                <div className="text-danger fs-7 mt-2">
                  {intl.formatMessage({id: 'USER_MANAGER.DELETE_MODAL.NO_ACCOUNT_SELECTED'})}
                </div>
              )}
            </>
          )}
        </Modal.Body>
        <Modal.Footer className="d-flex flex-column align-items-center">
          <div className="d-flex justify-content-center">
            <div className="me-4">
              <ButtonCancel onClick={() => setShowModal(false)} textId="CANCEL" />
            </div>
            <div>
              <ButtonSubmit
                textId="USER_MANAGER.DELETE_MODAL.SUBMIT"
                disabled={entityIds.length === 0}
                isLoading={isLoading}
              />
            </div>
          </div>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

export default ModalDeleteUser;
